import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { toast } from '@tempo-io/sui-sonner';
import { useSubscription } from '@trpc/tanstack-react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserProfileQuery } from '@/lib/queries/profile/useUserProfileQuery';
import { useOrgMemberByIdQuery } from '@/lib/queries/user-management/useOrgMemberByIdQuery';
import { userManagementQueryKeys } from '@/lib/query-keys/userManagement';
import { useTRPC } from '@/lib/trpc/trpc';

export const MembershipListener = () => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(true);
  const queryClient = useQueryClient();
  const router = useRouter();
  const { data: currentUser } = useUserProfileQuery();
  const { data: currentUserMembership } = useOrgMemberByIdQuery(currentUser?.id || '');
  const lastProcessedTimestampRef = useRef<Map<string, string>>(new Map());
  const errorCountRef = useRef(0);
  const maxRetries = 5;
  const trpc = useTRPC();
  useSubscription(
    trpc.membership.subscriptionOptions(undefined, {
      enabled: !!currentUser?.id && currentUserMembership && enabled,
      onData: async (trackedData) => {
        const { entity, timestamp, eventType } = trackedData.data;
        const { userId, organizationId, role } = entity;

        const eventKey = `${userId}-${organizationId}`;
        const lastTimestamp = lastProcessedTimestampRef.current.get(eventKey);
        if (lastTimestamp && timestamp <= lastTimestamp) {
          return;
        }
        lastProcessedTimestampRef.current.set(eventKey, timestamp);

        if (eventType === 'updated_full') {
          const currentRole = currentUserMembership?.roles?.at(0);
          const isNoLongerAdmin = currentRole === 'admin' && role !== 'admin';
          const isNowAdmin = currentRole !== 'admin' && role === 'admin';
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.profile() }),
            queryClient.invalidateQueries({
              queryKey: userManagementQueryKeys.userManagement.profile(userId),
            }),
            queryClient.invalidateQueries({ queryKey: ['permissions'] }),
          ]);
          if (isNoLongerAdmin) {
            toast.warning(t('userManagement.notifications.permissionsRevoked'), {
              description: t('userManagement.notifications.permissionsRevokedDescription'),
            });
          } else if (isNowAdmin) {
            toast.success(t('userManagement.notifications.permissionsUpgraded'), {
              description: t('userManagement.notifications.permissionsUpgradedDescription'),
            });
          }
          await router.invalidate();
        }
      },
      onConnectionStateChange: (connectionState) => {
        if (connectionState.state === 'connecting') {
          if (connectionState.error) {
            errorCountRef.current += 1;
            setEnabled(errorCountRef.current > maxRetries);
          } else {
            errorCountRef.current = 0;
          }
        }
      },
    }),
  );

  return null;
};
